import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { ParallaxImage } from "@/components/ParallaxImage";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, setSEO } from "@/components/Reveal";
import { CAPABILITIES, INFRASTRUCTURE, IMAGES } from "@/lib/data";

const CapabilityRows = () => (
    <section className="py-28 md:py-36" data-testid="capabilities-list">
        <div className="sf-container">
            <SectionHeading
                overline="What We Do"
                title="Dedicated Manufacturing Divisions"
                description="Four focused capability areas, one standard: precision at production scale."
            />
            <div className="mt-16 border-t border-white/10">
                {CAPABILITIES.map((c, i) => (
                    <Reveal key={c.num} delay={i * 0.06}>
                        <div className="group grid grid-cols-12 gap-4 items-baseline py-12 border-b border-white/10 px-2 md:px-6 hover:bg-sf-surface transition-colors duration-500" data-testid={`capabilities-row-${c.num}`}>
                            <span className="col-span-2 md:col-span-1 font-mono text-sf-green text-sm">{c.num}</span>
                            <h3 className="col-span-10 md:col-span-5 font-display font-bold text-2xl md:text-4xl text-white group-hover:text-sf-green transition-colors duration-500">
                                {c.title}
                            </h3>
                            <p className="col-span-10 col-start-3 md:col-span-5 md:col-start-8 text-sf-silver text-sm md:text-base leading-relaxed">{c.text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const ProcessStrengths = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="capabilities-strengths">
        <div className="sf-container grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
                <SectionHeading
                    overline="In-House Strength"
                    title="Technology on the Shop Floor"
                    description="Our facility pairs sophisticated machinery with disciplined process control — so volume never costs you tolerance."
                />
                <div className="mt-10 space-y-0 border-t border-white/10">
                    {INFRASTRUCTURE.map((item, i) => (
                        <Reveal key={item.title} delay={i * 0.08}>
                            <div className="py-6 border-b border-white/10 group" data-testid={`strength-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                <h3 className="font-display font-bold text-lg text-white group-hover:text-sf-green transition-colors duration-300">{item.title}</h3>
                                <p className="text-sf-silver text-sm mt-2 leading-relaxed">{item.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
            <Reveal delay={0.15}>
                <ParallaxImage src={IMAGES.machinery} alt="CNC tube bending machine" testid="capabilities-image" className="h-[560px]">
                    <div className="absolute bottom-0 left-0 bg-sf-green text-sf-black font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 z-10">
                        CNC Tube Bending
                    </div>
                </ParallaxImage>
            </Reveal>
        </div>
    </section>
);

const CapabilitiesCTA = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="capabilities-cta">
        <div className="sf-container flex flex-col lg:flex-row lg:items-end justify-between gap-10">
            <Reveal>
                <p className="sf-overline">Start a Program</p>
                <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white mt-5 max-w-2xl">
                    Have a Drawing? <span className="text-sf-green">We Build It.</span>
                </h2>
            </Reveal>
            <Reveal delay={0.15}>
                <Link to="/contact" data-testid="capabilities-cta-link" className="sf-btn-primary shrink-0">
                    Discuss Your Requirements <ArrowUpRight size={16} />
                </Link>
            </Reveal>
        </div>
    </section>
);

export default function Capabilities() {
    useEffect(() => {
        setSEO("Manufacturing Capabilities — Super Finishers", "Precision tubular components, automotive components, cycle parts and custom manufacturing solutions — engineered and mass-manufactured in India.");
    }, []);

    return (
        <>
            <PageHero
                overline="Manufacturing Capabilities"
                titleLines={["Built for", "Demanding Industries."]}
                description="Sophisticated machines, high-quality raw materials and disciplined engineering — from R&D to mass production."
                image="machinery"
            />
            <CapabilityRows />
            <ProcessStrengths />
            <CapabilitiesCTA />
        </>
    );
}
